import { APP_DISPLAY_NAME, type AuthUser } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Outlet } from 'react-router';

import { SkipLink } from '@/components/skip-link';
import { Toaster } from '@/components/ui/sonner';
import { UserMenu } from '@/features/nav/user-menu';
import { NotificationBell } from '@/features/notifications/notification-bell';
import { useRealtimeNotifications } from '@/features/notifications/use-realtime';
import { cn } from '@/lib/utils';
import { useMe } from '@/platform/auth/use-me';
import { BrandAccentEffect } from '@/platform/brand/brand-accent-effect';
import { PlatformBottomNav } from '@/platform/components/platform-bottom-nav';
import { PlatformSidebar } from '@/platform/components/platform-sidebar';
import { useRegisterPush } from '@/platform/features/notifications/push/use-register-push';
import {
  PlatformMainModeProvider,
  type PlatformMainMode,
} from '@/platform/layouts/platform-main-mode';

type PlatformChromeProps = {
  /** Brand / title block in the header (left side). Optional when `headerLeading` already owns it. */
  brand?: ReactNode;
  /** Optional controls before trailing actions (mobile product hamburger until step 3). */
  headerLeading?: ReactNode;
  children?: ReactNode;
  mainClassName?: string;
  footerLabel?: string;
  showFooter?: boolean;
  showBottomNav?: boolean;
  showSidebar?: boolean;
  userMenuProps?: {
    showPortalLink?: boolean;
    showAgentLink?: boolean;
    showPortalHomeLink?: boolean;
  };
  /** Extra nodes after the main chrome (e.g. portal chat widget). */
  after?: ReactNode;
};

/** Shared authenticated chrome: optional left rail, header without module selector, main. */
export function PlatformChrome(props: PlatformChromeProps) {
  return (
    <PlatformMainModeProvider>
      {(mainMode) => <PlatformChromeView {...props} mainMode={mainMode} />}
    </PlatformMainModeProvider>
  );
}

function PlatformChromeView({
  brand,
  headerLeading,
  children,
  mainClassName,
  footerLabel = APP_DISPLAY_NAME,
  showFooter = true,
  showBottomNav = true,
  showSidebar = true,
  userMenuProps,
  after,
  mainMode,
}: PlatformChromeProps & { mainMode: PlatformMainMode }) {
  const me = useMe();
  useRealtimeNotifications();
  const pushRegistration = useRegisterPush(Boolean(me.data));
  const fill = mainMode === 'fill';

  return (
    <div
      data-mobile-chrome
      className={cn(
        'flex max-w-full min-w-0 flex-col',
        fill ? 'h-dvh overflow-hidden' : 'min-h-svh',
        showBottomNav && 'pb-[calc(4.75rem+env(safe-area-inset-bottom))] lg:pb-0',
      )}
    >
      <BrandAccentEffect />
      {pushRegistration}
      <SkipLink />
      <div
        data-mobile-stage
        className={cn(
          'flex max-w-full min-w-0 flex-1',
          fill ? 'min-h-0 overflow-hidden' : 'min-h-svh',
        )}
      >
        {showSidebar && me.data ? <PlatformSidebar /> : null}
        <div className={cn('flex min-w-0 flex-1 flex-col', fill && 'min-h-0')}>
          <header className="sticky top-0 z-40 shrink-0 border-b border-border bg-card">
            <div className="flex h-14 min-w-0 items-center gap-2 px-3 sm:h-16 sm:gap-3 sm:px-4">
              {headerLeading}
              {brand ? <div className="min-w-0 flex-1">{brand}</div> : null}
              <div className="ml-auto flex shrink-0 items-center gap-1">
                {me.isPending ? (
                  <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
                ) : null}
                {me.data ? <HeaderActions user={me.data} {...userMenuProps} /> : null}
              </div>
            </div>
          </header>
          <main
            id="hauptinhalt"
            className={cn(
              'min-w-0 flex-1',
              fill
                ? 'flex min-h-0 flex-col overflow-hidden'
                : cn('container mx-auto px-4', mainClassName),
            )}
          >
            {children ?? <Outlet />}
          </main>
          {showFooter && !fill ? (
            <footer className="hidden border-t border-border py-4 text-center text-xs text-muted-foreground sm:block">
              {footerLabel}
            </footer>
          ) : null}
        </div>
      </div>
      {showBottomNav ? <PlatformBottomNav /> : null}
      <Toaster />
      {after}
    </div>
  );
}

function HeaderActions({
  user,
  showPortalLink,
  showAgentLink,
  showPortalHomeLink,
}: {
  user: AuthUser;
  showPortalLink?: boolean;
  showAgentLink?: boolean;
  showPortalHomeLink?: boolean;
}) {
  return (
    <>
      <NotificationBell />
      <UserMenu
        user={user}
        showPortalLink={showPortalLink}
        showAgentLink={showAgentLink}
        showPortalHomeLink={showPortalHomeLink}
      />
    </>
  );
}
